"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface SentimentAnalyzerProps {
  className?: string;
}

export default function SentimentAnalyzer({ className = "" }: SentimentAnalyzerProps) {
  const [text, setText] = useState("");
  const [language, setLanguage] = useState("pt-BR");
  const [analysis, setAnalysis] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const analyzeSentiment = async () => {
    if (!text.trim()) {
      addToast("Por favor, insira um texto para analisar", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/analyze-sentiment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, language }),
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
        addToast("Análise de sentimento concluída!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao analisar sentimento", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao analisar sentimento", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case "positivo": return "text-green-600";
      case "negativo": return "text-red-600";
      default: return "text-gray-600";
    }
  };

  const getSentimentEmoji = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case "positivo": return "😊";
      case "negativo": return "😔";
      default: return "😐";
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      <h3 className="text-2xl font-bold mb-6 text-gray-800">🧠 Analisador de Sentimento</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Texto para Análise
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent h-32"
            placeholder="Digite ou cole o texto que deseja analisar..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Idioma
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="pt-BR">Português (Brasil)</option>
            <option value="en-US">English (US)</option>
            <option value="es-ES">Español</option>
            <option value="fr-FR">Français</option>
          </select>
        </div>

        <button
          onClick={analyzeSentiment}
          disabled={isLoading || !text.trim()}
          className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? "Analisando..." : "Analisar Sentimento"}
        </button>

        {analysis && (
          <div className="mt-6 space-y-4">
            <h4 className="font-semibold text-gray-800">Resultados da Análise:</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-700 mb-2">Sentimento Geral</h5>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{getSentimentEmoji(analysis.sentiment)}</span>
                  <span className={`text-lg font-semibold ${getSentimentColor(analysis.sentiment)}`}>
                    {analysis.sentiment?.charAt(0).toUpperCase() + analysis.sentiment?.slice(1)}
                  </span>
                </div>
                <div className="mt-2">
                  <div className="text-sm text-gray-600">Confiança: {Math.round((analysis.confidence || 0) * 100)}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(analysis.confidence || 0) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-700 mb-2">Métricas</h5>
                <div className="space-y-1 text-sm">
                  <div>Polaridade: {analysis.polarity?.toFixed(2)}</div>
                  <div>Subjetividade: {Math.round((analysis.subjectivity || 0) * 100)}%</div>
                  <div>Comprimento: {analysis.metadata?.textLength || 0} caracteres</div>
                </div>
              </div>
            </div>

            {analysis.emotions && analysis.emotions.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-700 mb-2">Emoções Detectadas</h5>
                <div className="flex flex-wrap gap-2">
                  {analysis.emotions.map((emotion: string, index: number) => (
                    <span key={index} className="bg-accent text-white px-3 py-1 rounded-full text-sm">
                      {emotion}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {analysis.key_phrases && analysis.key_phrases.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-700 mb-2">Frases-Chave</h5>
                <div className="flex flex-wrap gap-2">
                  {analysis.key_phrases.map((phrase: string, index: number) => (
                    <span key={index} className="bg-secondary text-white px-3 py-1 rounded-full text-sm">
                      {phrase}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {analysis.analysis && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-700 mb-2">Análise Detalhada</h5>
                <p className="text-gray-700">{analysis.analysis}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
